/** @jsxImportSource @emotion/react */

import { FormEvent, useState } from 'react';
import { CSSObject } from '@emotion/react';

import {
  StripeError,
  Stripe,
  StripeElementsOptions,
  ConfirmationToken,
  CreateConfirmationToken,
} from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import useOverlay from '../hooks/useOverlay';
import useLanguage from '../hooks/useLanguage';
import { IBaseOverlay } from '../contexts/overlayContext';
import Clickable from '../components/clickable';
import useTheme from '../hooks/useTheme';
import environment from '../utils/environment';

export interface IManageCardSheetData {
  currency: string;
  customerSessionClientSecret: string;
  paymentMethodTypes: string[];
  publishableKey: string;
}

export interface IStripeManageCardOverlay extends IBaseOverlay {
  stripe: Stripe;
  stripeParameters: Partial<IManageCardSheetData> &
    Pick<IManageCardSheetData, 'currency' | 'customerSessionClientSecret'>;
  onConfirmationToken: (confirmationToken: ConfirmationToken) => void;
  onError: (error: StripeError) => void;
  // Stripe's own "remove saved card" affordance inside the Payment Element removes a card
  // immediately against Stripe, client-side - independently of Save. Cancel must still signal
  // completion so the checkout screen reloads and picks up any such removal.
  onCancel: () => void;
}

interface IManageCardForm {
  onConfirmationToken: (confirmationToken: ConfirmationToken) => void;
  onError: (error: StripeError) => void;
  onCancel: () => void;
}

// Unlike the payment checkout form, there's no "save this card" checkbox here - in Stripe's
// setup mode, confirming the form *is* the save action, there's nothing else it could mean.
const ManageCardForm = ({
  onConfirmationToken,
  onError,
  onCancel,
}: IManageCardForm) => {
  const stripe = useStripe();
  const elements = useElements();

  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeValid, setStripeValid] = useState(false);

  const { font, color } = useTheme();
  const overlay = useOverlay();
  const language = useLanguage();

  const FORM_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    backgroundColor: color.LOCAL_WHITE,
    padding: '30px 20px 10px',
    borderRadius: 7,

    '.StripeElement': {
      boxSizing: 'border-box',
      paddingTop: '20px',
      paddingBottom: '10px',
      borderTop: `1px solid ${color.DIVIDER_LINE}`,
    },
  };

  const HEADER_STYLE: CSSObject = {
    fontSize: 17,
    fontFamily: font.FAMILY_BOLD,
    lineHeight: '21px',
    marginBottom: 20,
  };

  const CANCEL_BUTTON_STYLE: CSSObject = {
    backgroundColor: color.LOCAL_WHITE,
    color: color.PRIMARY,
    '&[active], :disabled': {
      backgroundColor: color.LOCAL_WHITE,
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setStripeBusy(true);

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        onError(submitError as StripeError);
        setStripeBusy(false);
        return;
      }

      const confirmTokenOptions: CreateConfirmationToken = {
        elements,
        params: {
          return_url: `${window.location.origin}/stripe-redirect.html`,
        },
      };

      const { error, confirmationToken } = await stripe.createConfirmationToken(
        confirmTokenOptions
      );

      if (error) {
        onError(error);
        setStripeBusy(false);
        return;
      }

      if (confirmationToken) {
        onConfirmationToken(confirmationToken);
      }
    } catch (error) {
      onError(error as StripeError);
      setStripeBusy(false);
    }
  };

  const appMarket = environment.COUNTRY;

  return (
    <form onSubmit={handleSubmit} css={FORM_STYLE}>
      {/* Dialog header */}
      <h2 css={HEADER_STYLE}>{language.get('save_card_details')}</h2>
      <PaymentElement
        id='manage-card-payment-element'
        options={{
          defaultValues: {
            billingDetails: {
              address: {
                country: appMarket ?? undefined, // let Stripe fallback if appMarket is falsy,
              },
            },
          },
          wallets: { applePay: 'never', googlePay: 'never' },
          layout: { type: 'tabs', defaultCollapsed: false },
        }}
        onChange={(event) => setStripeValid(event.complete)}
      />

      {/* Save button */}
      <Clickable
        styleAs='button'
        defaultHandler
        disabled={stripeBusy || !stripeValid}
      >
        {language.get('save')}
      </Clickable>

      {/* Cancel button */}
      <Clickable
        type='button'
        styleAs='button'
        disabled={stripeBusy}
        css={CANCEL_BUTTON_STYLE}
        onClick={() => {
          overlay.dismiss();
          onCancel();
        }}
      >
        {language.get('cancel')}
      </Clickable>
    </form>
  );
};

const StripeManageCardOverlay = ({
  stripe,
  stripeParameters,
  onConfirmationToken,
  onError,
  onCancel,
}: IStripeManageCardOverlay) => {
  const overlay = useOverlay();
  const { color } = useTheme();

  const options: StripeElementsOptions = {
    mode: 'setup',
    currency: stripeParameters.currency.toLowerCase(),
    paymentMethodTypes: stripeParameters.paymentMethodTypes,
    customerSessionClientSecret: stripeParameters.customerSessionClientSecret,
    // Must match the SetupIntent's own usage (StripeGateway.createSetupIntent) - the
    // confirmation token this produces carries its own setup_future_usage, and Stripe
    // rejects confirming a SetupIntent whose usage doesn't match it.
    setupFutureUsage: 'on_session',
    appearance: {
      variables: {
        colorPrimary: color.PRIMARY,
        colorDanger: color.ALERT,
      },
    },
  };

  return (
    <div>
      <Elements stripe={stripe} options={options}>
        <ManageCardForm
          onConfirmationToken={(confirmationToken) => {
            overlay.dismiss();
            onConfirmationToken(confirmationToken);
          }}
          onError={(error) => {
            overlay.dismiss();
            onError(error);
          }}
          onCancel={onCancel}
        />
      </Elements>
    </div>
  );
};

export default StripeManageCardOverlay;
